import { useState, useMemo } from "react";
import DocumentOutlineIcon from "shared/components/icons/DocumentOutlineIcon";
import PillarForm from "shared/components/pillar-form/PillarForm";
import { usePillarModalControl } from "shared/components/pillar-modal/usePillarModalControl";
import PillarTable from "shared/components/pillar-table/PillarTable";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { type HubDocumentPageDocumentType } from "shared/types/HubPageRouteParams";
import { TextInput } from "hub/src/components/common/filter/dynamic-filter-inputs";
import HubDocumentViewerModal from "hub/src/components/documents/HubDocumentViewerModal";

interface HubDocumentsTableProps {
  title?: string;
  documents: HubDocumentPageDocumentType[];
}

const HubDocumentsTable: React.FC<HubDocumentsTableProps> = ({
  title,
  documents,
}) => {
  const session = useCustomSession();

  const [currentDocument, setCurrentDocument] =
    useState<HubDocumentPageDocumentType | null>(null);

  const [documentsSearchValue, setDocumentsSearchValue] = useState<string>("");

  const control = usePillarModalControl({
    onCloseCallback: () => setCurrentDocument(null),
  });

  const filteredDocuments = useMemo(() => {
    if (!documents || !documents.length) {
      return [];
    }
    if (!documentsSearchValue) {
      return documents;
    }

    return documents.filter((document) =>
      document.title.toLowerCase().includes(documentsSearchValue.toLowerCase()),
    );
  }, [documents, documentsSearchValue]);

  return (
    <div className="container-page-padding">
      {title && <h2 className="container-page-header">{title}</h2>}
      <div className="container-page-content">
        {currentDocument && (
          <HubDocumentViewerModal
            key={currentDocument.href}
            currentDocument={currentDocument}
            control={control}
          />
        )}
        <TextInput
          className="w-full enabled:pl-5 enabled:pr-2 min-sm:w-1/2 min-md:w-1/3 min-lg:w-35"
          iconClassName="w-3 h-3"
          wrapperClassName="mb-3"
          name={"documentSearch"}
          testid="document-search-bar"
          currentValue={documentsSearchValue}
          onChange={(inputName, value) => {
            setDocumentsSearchValue(value as string);
          }}
        />
        <PillarTable
          pillarTableContext={{
            tableRowResults: filteredDocuments,
          }}
          queryKey={[
            "GetDocuments",
            String(session.profile!.profileId),
            documentsSearchValue,
          ]}
          handleRowClick={(row) => {
            setCurrentDocument(row);
            control.setIsOpen(true);
          }}
          className="w-full"
          pagination
        >
          <PillarTable.Column<HubDocumentPageDocumentType>
            heading="Document Name"
            contentClassName="w-full"
          >
            {(file) => (
              <div className="w-full grid grid-cols-[auto_1fr_auto] items-center gap-x-1 p-3 rounded-lg cursor-pointer bg-neutral-mid-100">
                <DocumentOutlineIcon />

                <div className="text-lg font-medium line-clamp-2 whitespace-break-spaces break-words">
                  {file.title}
                </div>

                <PillarForm.Button
                  testid="document-preview-button"
                  className="flex button-small-general-filled"
                  title="Preview"
                  label="Preview"
                />
              </div>
            )}
          </PillarTable.Column>
        </PillarTable>
      </div>
    </div>
  );
};

export default HubDocumentsTable;
